import React from "react";
import { cn } from "@/lib/utils";
import helicopterPartsIcon from "@/assets/parts-helicopter-cart.png";
import enginePartsIcon from "@/assets/parts-engine-cart.png";
import generalPartsIcon from "@/assets/parts-general-cart.png";
import rentalPartsIcon from "@/assets/parts-rental-cart.png";

export type PartsType =
  | "parts_sell"
  | "parts_sell_aircraft"
  | "parts_sell_engine"
  | "parts_sell_general"
  | "parts_rent"
  | "parts_buy"
  | "parts_buy_aircraft"
  | "parts_buy_engine"
  | "parts_buy_general"
  | "parts_rent_request";

interface PartsIconProps {
  partsType: PartsType;
  size?: "sm" | "md" | "lg" | "xl" | "hero";
  className?: string;
  context?: 'landing' | 'navigation';
  /** Density mode: 'card' renders the inner artwork larger (90% vs 80%) for wizard cards */
  density?: 'default' | 'card';
}

const sizeClasses = {
  sm: "w-12 h-12",
  md: "w-16 h-16",
  lg: "w-24 h-24",
  xl: "w-32 h-32 md:w-40 md:h-40",
  hero: "w-40 h-40 md:w-48 md:h-48",
};

// Map parts type to the appropriate cart icon
const getIconForType = (partsType: PartsType): string => {
  if (partsType.includes("aircraft")) return helicopterPartsIcon;
  if (partsType.includes("engine")) return enginePartsIcon;
  if (partsType === "parts_rent" || partsType === "parts_rent_request") return rentalPartsIcon;
  return generalPartsIcon; // general types and fallback
};

export const PartsIcon: React.FC<PartsIconProps> = ({
  partsType,
  size = "md",
  className,
  context = 'landing',
  density = 'default',
}) => {
  const iconSrc = getIconForType(partsType);
  
  // Filter classes for navigation context (dark icons on white background, inverted in dark mode)
  const filterClass = context === 'navigation'
    ? "brightness-0 dark:invert"
    : "brightness-0 invert";
  
  const innerScale = density === 'card' ? 'w-[90%] h-[90%]' : 'w-[80%] h-[80%]';

  return (
    <div
      className={cn(
        "relative flex items-center justify-center",
        sizeClasses[size],
        className
      )}
    >
      <img
        src={iconSrc}
        alt="Parts"
        className={cn("object-contain", innerScale, filterClass)}
      />
    </div>
  );
};

export default PartsIcon;
